import React, { useEffect, useState } from 'react';
import Section, { SectionBody, SectionBodyText, SectionHeading } from '../../components/Section';
import BackToTop from '../../components/BackToTop';
import { DataTable, DataTableBody, DataTableCell, DataTableRow } from '../../components/DataTable';
import { espressoEquipment } from '../../data/espressoEquipment';
import { setMetaTags, getFirstImageFromItems } from '../../utils/metaTags';

interface EspressoProps {
    theme: string;
}

interface EspressoItem {
    name: string;
    link: string;
    price: string;
    image?: string;
}

const Espresso: React.FC<EspressoProps> = ({ theme }) => {
    const [showAll, setShowAll] = useState(false);
    const visibleItems = showAll ? espressoEquipment : espressoEquipment.slice(0, 7);
    
    // Set up metadata for this page
    useEffect(() => {
        const heroImage = getFirstImageFromItems(espressoEquipment);
        setMetaTags({
            title: 'Espresso Setup',
            description: 'My home espresso setup and recommendations. Lelit Bianca v3 with DF64 grinder and complete workflow.',
            imageUrl: heroImage,
            url: window.location.href
        });
    }, []);
    
    useEffect(() => {
        // Add event listener for anchor links
        document.addEventListener('click', handleAnchorClick as EventListener);
        return () => {
            document.removeEventListener('click', handleAnchorClick as EventListener);
        };
    }, []);
    
    // Handle smooth scrolling for anchor links
    const handleAnchorClick = (event: Event) => {
        const target = event.target as HTMLElement;
        if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
            event.preventDefault();
            const id = target.getAttribute('href')?.substring(1);
            const element = document.getElementById(id || '');
            if (element) {
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - 144;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
                // Update URL without page reload
                window.history.pushState(null, '', target.getAttribute('href') || '');
            }
        }
    };
    
    // Handle showing more items
    const handleShowMore = () => {
        const wasShowingAll = showAll;
        setShowAll(!showAll);
        
        setTimeout(() => {
            if (!wasShowingAll) {
                // Scroll to the first new item
                const firstNewItem = document.querySelector('.espresso-table tr:nth-child(7)');
                if (firstNewItem) {
                    firstNewItem.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }
        }, 0);
    };

    return (
        <div>
            <Section id="espresso">
                <SectionHeading title="Espresso kit list" id="kit-list" />
                <SectionBody>
                    <SectionBodyText>
                    My current setup is a Lelit Bianca v3 paired with a DF64 Gen 2 grinder. I typically go with 19g in, 38g out (or 57g for a lungo) over 25-35 seconds. I opt for specialty third-wave roasters: <a href="https://skcoffeeplease.com/" target="_blank" rel="noopener noreferrer" className="text-lime-500 hover:text-lime-400 transition-colors">SK Coffee</a>, <a href="https://www.dakcoffeeroasters.com/" target="_blank" rel="noopener noreferrer" className="text-lime-500 hover:text-lime-400 transition-colors">Dak</a>, and <a href="https://coffeecollective.dk/" target="_blank" rel="noopener noreferrer" className="text-lime-500 hover:text-lime-400 transition-colors">Coffee Collective</a>. See my <a href="#full-kit" className="text-lime-500 hover:text-lime-400 transition-colors">set up</a> below.                    </SectionBodyText>
                    
                    {/* Hero image - first equipment item */}
                    {espressoEquipment[0]?.image && (
                        <div className="mt-6 mb-6">
                            <img 
                                src={espressoEquipment[0].image}
                                alt="Lelit Bianca v3 Espresso Machine"
                                className="w-full max-w-md mx-auto rounded-lg shadow-lg"
                            />
                        </div>
                    )}
                    
                    <SectionHeading title="My full kit list:" id="full-kit" />
                    
                    {/* <SectionBodyText>
                    After 15 years as a coffee enthusiast, I finally took the plunge into home espresso. Why now? A mix of practicality and curiosity.
                    </SectionBodyText>
                    <ul className="list-disc list-inside">
                    <li>Less liquid per day = fewer bathroom trips</li>
                    <li>A shared hobby with my mom</li>
                    <li>Teeth whitening and enamel preservation</li>
                    <li>A humbling learning curve and skill</li>
                    <li>A daily ritual</li>
                    </ul>
                    <SectionBodyText>    
                        As many espresso fanatics will tell you, this hobby has become more of an obsession at this point. I would go as far to say it has challenged most of what I believed about coffee before; that coffee shouldn't be fancy or overly contrived. It's a shame that I considered anyone who dabbled in fancy coffee lame (more below on coffee that I believe is still <a href="#hits-diff" className="text-lime-500 hover:text-lime-500 transition-colors duration-200">not worth drinking</a> and to balance that out, coffee that just plainly hits different). Anyone who has reached the end of the bell curve will tell you enlightenment is a full circle exercise.
                    </SectionBodyText> */}  
                    <div className="relative">
                        <DataTable className="espresso-table" columnWidths={[undefined, '4.75rem']}>
                            <DataTableBody>
                                {visibleItems.map((item: EspressoItem, index: number) => {
                                    const isRetired = item.name.includes('Apexstone')
                                    return (
                                        <DataTableRow key={index}>
                                            <DataTableCell truncate>
                                                <div className="flex items-center gap-x-2 min-w-0">
                                                    <div className={`w-8 h-8 md:w-9 md:h-9 bg-gray-600 rounded-[12px] overflow-hidden flex-shrink-0 ${isRetired ? 'opacity-40' : ''}`}>
                                                        {item.image && (
                                                            <img
                                                                src={item.image}
                                                                alt={item.name}
                                                                className={`w-full h-full object-cover ${isRetired ? 'opacity-60' : ''}`}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-1 overflow-hidden">
                                                        <a
                                                            href={item.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={`transition-colors truncate block ${isRetired ? 'line-through text-gray-600 group-hover:text-gray-500' : 'text-white group-hover:text-lime-500'}`}
                                                        >
                                                            {item.name}
                                                        </a>
                                                    </div>
                                                </div>
                                            </DataTableCell>
                                            <DataTableCell align="right" className="relative z-10">
                                                <span className={`transition-colors ${isRetired ? 'line-through text-gray-600 group-hover:text-gray-500' : 'text-white group-hover:text-lime-500'}`}>
                                                    {item.price ? item.price : ''}
                                                </span>
                                            </DataTableCell>
                                        </DataTableRow>
                                    )
                                })}
                            </DataTableBody>
                        </DataTable>

                        {espressoEquipment.length > 7 && (
                            <>
                                {!showAll && (
                                    <div className="absolute bottom-0 left-0 right-0 h-56 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" />
                                )}
                                <div className="relative z-10">
                                    <button
                                        onClick={handleShowMore}
                                        className="mt-2 mb-6 ml-0 text-left text-lime-500 hover:text-lime-400 transition-colors"
                                    >
                                        {showAll ? 'Show less' : `See more (${espressoEquipment.length - 7})`}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </SectionBody>
            </Section>
            
            
            

            {/* Add TimeStamp at the bottom */}
            {/* <div className="mt-8">
                <TimeStamp />
            </div> */}

            <BackToTop theme={theme} />
        </div>
    );
};

export default Espresso; 